// Who looks after an order (orders.server_id): the circle with their initials on the
// floor, the "Server · Change" row on an order, and the dialog that changes it. A
// waiter takes a table for themselves; only a manager gives one to someone else (the
// server refuses anything else). Canvas boards ServerFloor and ServerChange.
import type { OrderRow } from '@ims/core'
import { Button, Modal, cx } from '@ims/ui'
import { useCallback, useState } from 'react'
import { initials, useServers, type StaffMember } from '../lib/staff.ts'
import { useUser } from '../session.tsx'
import { useCan } from '../workspace.tsx'
import type { Floor } from './floor.ts'

const ROLE_WORD: Record<string, string> = {
  waiter: 'Waiter',
  staff: 'Staff',
  manager: 'Manager',
  admin: 'Admin',
  owner: 'Owner',
}

/** The initials circle: one neutral look for everyone, never told apart by colour. */
export function ServerAvatar({
  name,
  size = 'md',
  className,
}: {
  name: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  return (
    <span
      aria-hidden
      className={cx(
        'grid shrink-0 place-items-center rounded-full border-[1.5px] border-border bg-surface font-bold tracking-wide text-ink',
        size === 'sm' && 'size-6 text-[9.5px]',
        size === 'md' && 'size-8 text-[11px]',
        size === 'lg' && 'size-9 text-xs',
        className,
      )}
    >
      {initials(name)}
    </span>
  )
}

/** The signed-in user's own name: the cached team list, else their account. */
export function useMyName(): string {
  const user = useUser()
  const servers = useServers()
  const meta = user.user_metadata as { full_name?: string } | undefined
  return (
    servers.find((s) => s.id === user.id)?.name ??
    meta?.full_name?.trim() ??
    user.email?.split('@')[0] ??
    'Me'
  )
}

/** A server's name, or null when this device has not seen them yet. */
export function useServerName(): (id: string | null) => string | null {
  const servers = useServers()
  const me = useUser().id
  const myName = useMyName()
  return useCallback(
    (id) =>
      id === null ? null : id === me ? myName : (servers.find((s) => s.id === id)?.name ?? null),
    [servers, me, myName],
  )
}

/** "Server: Sita Rai · Change" on an order. */
export function ServerRow({
  serverId,
  canChange,
  onChange,
  className = 'mt-3',
}: {
  serverId: string | null
  canChange: boolean
  onChange: () => void
  className?: string
}) {
  const name = useServerName()(serverId)
  return (
    <div
      className={cx(
        className,
        'flex items-center gap-2.5 rounded-xl border border-border bg-surface py-1.5 pr-1.5 pl-2.5',
      )}
    >
      {name ? <ServerAvatar name={name} /> : null}
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block text-xs text-muted">Server</span>
        <b className="block truncate text-sm font-semibold">{name ?? 'Not known on this device'}</b>
      </span>
      {canChange && (
        <Button size="sm" variant="secondary" onClick={onChange}>
          Change
        </Button>
      )}
    </div>
  )
}

/** Who looks after `order` now, and who to give it to. */
export function ServerDialog({
  title,
  order,
  floor,
  busy,
  onClose,
  onPick,
}: {
  /** The table's name or the order's ("T7", "Takeaway #13"). */
  title: string
  order: OrderRow
  floor: Floor | undefined
  busy: boolean
  onClose: () => void
  onPick: (serverId: string) => void
}) {
  const me = useUser().id
  const myName = useMyName()
  const isManager = useCan('manager')
  const servers = useServers()
  // The signed-in user is always on the list, even before the team has been fetched.
  const people: StaffMember[] = servers.some((s) => s.id === me)
    ? servers
    : [{ id: me, name: myName, role: null }, ...servers]
  const [picked, setPicked] = useState<string>(
    order.server_id === me || !isManager ? me : (order.server_id ?? me),
  )
  const tablesOf = (id: string) =>
    new Set(
      (floor?.orders ?? [])
        .filter((o) => o.order.server_id === id && o.order.service_type === 'dine_in')
        .map((o) => o.order.table_id),
    ).size
  const pickedName = people.find((p) => p.id === picked)?.name ?? ''
  const unchanged = picked === order.server_id
  return (
    <Modal
      open
      onClose={onClose}
      title={`Server for ${title}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={unchanged} onClick={() => onPick(picked)}>
            {picked === me ? `Take ${title}` : `Give ${title} to ${pickedName}`}
          </Button>
        </>
      }
    >
      <p className="-mt-1 mb-3 text-sm text-muted">
        Who looks after this table. Anyone can still add dishes to it.
      </p>
      <div role="radiogroup" aria-label="Server" className="flex flex-col gap-2">
        {people.map((p) => {
          const checked = picked === p.id
          const allowed = isManager || p.id === me
          const n = tablesOf(p.id)
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={checked}
              disabled={!allowed}
              onClick={() => setPicked(p.id)}
              className={cx(
                'flex min-h-14 items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-55',
                checked
                  ? 'border-primary bg-primary-light ring-1 ring-primary'
                  : 'border-border enabled:hover:bg-soft',
              )}
            >
              <ServerAvatar name={p.name} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <b className="truncate text-sm font-semibold text-ink">
                    {p.name}
                    {p.id === me && <span className="font-normal text-muted"> (you)</span>}
                  </b>
                  {p.id === order.server_id && (
                    <span className="rounded-full bg-soft px-2 py-px text-[11.5px] font-bold text-muted">
                      Now
                    </span>
                  )}
                </span>
                <span className="block text-xs text-muted">
                  {[
                    p.role ? ROLE_WORD[p.role] : null,
                    n === 0 ? 'no tables' : `${n} ${n === 1 ? 'table' : 'tables'} now`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span
                aria-hidden
                className={cx(
                  'size-5 shrink-0 rounded-full',
                  checked
                    ? 'bg-primary shadow-[inset_0_0_0_5px_var(--color-primary),inset_0_0_0_8px_var(--color-surface)]'
                    : 'border-[1.5px] border-subtle',
                )}
              />
            </button>
          )
        })}
      </div>
      <p className="mt-3 rounded-xl bg-soft px-3 py-2.5 text-xs leading-relaxed text-muted">
        {isManager
          ? 'A waiter can take a table for themselves. Giving it to someone else is for a manager. The bill prints “Served by” with this name.'
          : 'You can take a table for yourself. Ask a manager to give a table to someone else. The bill prints “Served by” with this name.'}
      </p>
    </Modal>
  )
}
